/**
 * Species Counts Hook
 * Provides cached species counts for filter badges
 * Uses localStorage caching with 2-minute TTL
 */

import { useState, useEffect, useMemo } from 'react';
import { rpcCatalogCountV2 } from '@/lib/api/main';
import { cacheConfig, getCachedData, setCacheData } from '@/lib/config/cacheConfig';
import { logger, createRequestContext } from '@/lib/services';

interface SpeciesCounts {
  dog: number;
  cat: number;
}

interface BaseFilters {
  breedIds: string[];
  sex: string[];
  ageBuckets: string[];
  geoPlaceId: string | null;
  sizes: string[];
  colors: string[];
  healthSpecialNeeds: boolean | null;
}

interface CachedSpeciesCounts {
  [cacheKey: string]: SpeciesCounts;
}

/**
 * Generate stable cache key from filters
 */
function generateCacheKey(filters: BaseFilters): string {
  return JSON.stringify({
    breeds: [...filters.breedIds].sort(),
    geo: filters.geoPlaceId,
    sex: [...filters.sex].sort(),
    ages: [...filters.ageBuckets].sort(),
    sizes: [...filters.sizes].sort(),
    colors: [...filters.colors].sort(),
    health: filters.healthSpecialNeeds,
  });
}

/**
 * Get cached counts for a specific filter key
 */
function getCachedCounts(cacheKey: string): SpeciesCounts | null {
  const { ttlMs, storageKey } = cacheConfig.speciesCounts;
  const allCached = getCachedData<CachedSpeciesCounts>(storageKey, ttlMs);
  return allCached?.[cacheKey] ?? null;
}

/**
 * Set cached counts for a specific filter key
 */
function setCachedCounts(cacheKey: string, counts: SpeciesCounts): void {
  const { ttlMs, storageKey } = cacheConfig.speciesCounts;
  const existing = getCachedData<CachedSpeciesCounts>(storageKey, ttlMs) ?? {};
  
  // Keep max 10 cached filter combinations to limit storage
  const keys = Object.keys(existing);
  if (keys.length >= 10) {
    delete existing[keys[0]];
  }
  
  setCacheData(storageKey, { ...existing, [cacheKey]: counts });
}

/**
 * Hook for fetching species counts with caching and debounce
 */
export function useSpeciesCounts(filters: BaseFilters): SpeciesCounts {
  const [counts, setCounts] = useState<SpeciesCounts>({ dog: 0, cat: 0 });

  // Stable cache key based on filter values
  const cacheKey = useMemo(() => generateCacheKey(filters), [
    JSON.stringify(filters.breedIds),
    JSON.stringify(filters.sex),
    JSON.stringify(filters.ageBuckets),
    filters.geoPlaceId,
    JSON.stringify(filters.sizes),
    JSON.stringify(filters.colors),
    filters.healthSpecialNeeds,
  ]);

  // Build stable RPC params
  const rpcParams = useMemo(() => ({
    breed_ids: filters.breedIds.length > 0 ? filters.breedIds : null,
    sex: filters.sex.length > 0 ? filters.sex : null,
    age_buckets: filters.ageBuckets.length > 0 ? filters.ageBuckets : null,
    geo_place_id: filters.geoPlaceId ?? null,
    sizes: filters.sizes.length > 0 ? filters.sizes : null,
    colors: filters.colors.length > 0 ? filters.colors : null,
    health_special_needs: filters.healthSpecialNeeds ?? null,
  }), [cacheKey]);

  useEffect(() => {
    // Check cache first
    const cached = getCachedCounts(cacheKey);
    if (cached) {
      setCounts(cached);
      return;
    }

    const { trace_id: traceId, request_id: requestId } = createRequestContext();

    // Debounce + fetch
    const timeoutId = setTimeout(async () => {
      try {
        const [dogCount, catCount] = await Promise.all([
          rpcCatalogCountV2({ ...rpcParams, species_key: 'dog' }),
          rpcCatalogCountV2({ ...rpcParams, species_key: 'cat' }),
        ]);

        const result: SpeciesCounts = { dog: dogCount, cat: catCount };
        setCounts(result);
        setCachedCounts(cacheKey, result);
      } catch (error) {
        logger.error('Failed to fetch species counts', {
          context: { feature: 'catalog', function_name: 'useSpeciesCounts', trace_id: traceId, request_id: requestId },
          error: error as Error,
          error_code: 'species_count_failed',
          error_type: 'unexpected',
        });
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [cacheKey, rpcParams]);

  return counts;
}

export default useSpeciesCounts;
